<template>
    <div id="employeepage">
        <actions-menu>
            <actions-menu-item
                :action="() => $router.push('/employees')"
                icon="arrow-left"
                class="back"
                :label="trans('pages.employee_workhours.back')"
            />
            <actions-menu-item
                :action="exportWorkhours"
                icon="table"
                :label="trans('pages.employee_workhours.export')"
            />
            <actions-menu-item
                :action="exportTargetTimes"
                icon="history"
                :label="trans('pages.employee_workhours.export_target_times')"
            />
        </actions-menu>
        <div class="filtering">
            <div class="filters">
                <div class="filter">
                    <ui5-label for="workhours-employee">
                        {{ trans('pages.employee_workhours.employee') }}
                    </ui5-label>
                    <ui5-select
                        id="workhours-employee"
                        @change="
                            selectedEmployee =
                                $event.detail.selectedOption.value
                        "
                    >
                        <ui5-option
                            value="*"
                            :selected="selectedEmployee == '*'"
                        >
                            {{
                                trans('pages.employee_workhours.all_employees')
                            }}
                        </ui5-option>
                        <template v-if="employees">
                            <ui5-option
                                v-for="employee in employees"
                                :value="'' + employee.ID"
                                :selected="selectedEmployee == employee.ID"
                                :key="'employee.' + employee.ID"
                            >
                                {{ employee.name }}
                            </ui5-option>
                        </template>
                    </ui5-select>
                </div>

                <div class="filter">
                    <ui5-label for="workhours-year">
                        {{ trans('pages.employee_workhours.year') }}
                    </ui5-label>
                    <ui5-select
                        id="workhours-year"
                        @change="
                            selectedYear = $event.detail.selectedOption.value
                        "
                    >
                        <ui5-option
                            v-if="yearOptions"
                            v-for="year in yearOptions"
                            :key="'year.' + year"
                            :value="year"
                            :selected="selectedYear == year"
                        >
                            {{ year }}
                        </ui5-option>
                    </ui5-select>
                </div>

                <div class="filter">
                    <ui5-label for="workhours-month">
                        {{ trans('pages.employee_workhours.month') }}
                    </ui5-label>
                    <ui5-select
                        id="workhours-month"
                        @change="
                            selectedMonth = $event.detail.selectedOption.value
                        "
                    >
                        <template v-if="months">
                            <ui5-option
                                v-for="(month, index) in months"
                                :key="'month.' + index"
                                :value="index + 1"
                                :selected="index + 1 == selectedMonth"
                            >
                                {{ month }}
                            </ui5-option>
                        </template>
                    </ui5-select>
                </div>

                <div class="filter">
                    <ui5-label for="workhours-sortby">
                        {{ trans('pages.employee_workhours.sort_by') }}
                    </ui5-label>
                    <ui5-select
                        id="workhours-sortby"
                        @change="sortBy = $event.detail.selectedOption.value"
                    >
                        <ui5-option
                            value="day"
                            :selected="sortBy == 'day'"
                        >
                            {{ trans('pages.employee_workhours.day') }}
                        </ui5-option>
                        <ui5-option
                            value="name"
                            :selected="sortBy == 'name'"
                        >
                            {{ trans('pages.employee_workhours.employee') }}
                        </ui5-option>
                    </ui5-select>
                </div>

                <ui5-button
                    id="workhours-filter-button"
                    @click="showWorkhours"
                >
                    {{ trans('pages.employee_workhours.filter') }}
                </ui5-button>
            </div>
        </div>

        <div class="workhours-overview-line">
            <div class="workhours-overview-column hash-column"><b>#</b></div>
            <div class="workhours-overview-column">
                <b>{{ trans('pages.employee_workhours.employee_column') }}</b>
            </div>
            <div class="workhours-overview-column">
                <b>{{ trans('pages.employee_workhours.day_column') }}</b>
            </div>
            <div class="workhours-overview-column">
                <b>{{ trans('pages.employee_workhours.start_workday') }}</b>
            </div>
            <div class="workhours-overview-column">
                <b>{{ trans('pages.employee_workhours.start_lunch_break') }}</b>
            </div>
            <div class="workhours-overview-column">
                <b>{{ trans('pages.employee_workhours.end_lunch_break') }}</b>
            </div>
            <div class="workhours-overview-column">
                <b>{{ trans('pages.employee_workhours.end_workday') }}</b>
            </div>
            <div class="workhours-overview-column">
                <b>{{ trans('pages.employee_workhours.smoke_breaks') }}</b>
            </div>
            <div class="workhours-overview-column">
                <b>{{ trans('pages.employee_workhours.total') }}</b>
            </div>
            <div class="workhours-overview-column">
                <b>{{ trans('pages.employee_workhours.absence_reason') }}</b>
            </div>
        </div>

        <div
            id="workhours-list"
            v-if="workhours && totalSecs !== null"
        >
            <div
                class="workhours-overview-line"
                v-for="(workhour, workhourIndex) in workhours"
                :key="'workhour.' + workhourIndex"
            >
                <div class="workhours-overview-column hash-column">
                    {{ workhour.number == null ? '-' : workhour.number }}
                </div>
                <div
                    class="workhours-overview-column"
                    v-if="workhour.vacation == ''"
                >
                    <a
                        @click="
                            $router.push(
                                '/employeedetails/' + workhour.employee.ID
                            )
                        "
                    >
                        {{ workhour.employee.name }}
                    </a>
                </div>
                <div
                    class="workhours-overview-column"
                    v-else
                >
                    <a
                        @click="
                            $router.push(
                                '/employeedetails/' + workhour.employee.ID
                            )
                        "
                    >
                        {{ workhour.employee.name }} ({{ workhour.vacation }})
                    </a>
                </div>
                <div class="workhours-overview-column">
                    {{ moment(workhour.day).format('DD-MM-YYYY') }}
                    {{
                        workhour.number == null
                            ? ''
                            : ' (#' + workhour.number + ')'
                    }}
                </div>
                <div class="workhours-overview-column">
                    <input
                        type="text"
                        @blur="updateDayStartEmployee(workhour)"
                        v-model="workhour.dayStart"
                    />
                </div>
                <div class="workhours-overview-column">
                    <input
                        type="text"
                        @blur="updatePauseStartEmployee(workhour)"
                        v-model="workhour.pauseStart"
                    />
                </div>
                <div class="workhours-overview-column">
                    <input
                        type="text"
                        @blur="updatePauseEndEmployee(workhour)"
                        v-model="workhour.pauseEnd"
                    />
                </div>
                <div class="workhours-overview-column">
                    <input
                        type="text"
                        @blur="updateDayEndEmployee(workhour)"
                        v-model="workhour.dayEnd"
                    />
                </div>
                <div class="workhours-overview-column">
                    {{ workhour.totalBreaks }}
                </div>
                <div class="workhours-overview-column">
                    {{ workhour.totalWorked }}
                </div>
                <div
                    v-if="workhour.error"
                    class="workhours-overview-column"
                >
                    <img
                        src="images/error.png"
                        class="error-icon"
                        alt="Herstel"
                        @click="restore(workhour)"
                    />
                </div>
                <div
                    v-if="workhour.saving"
                    class="workhours-overview-column"
                >
                    <img src="images/ajax-loader.gif" />
                </div>
                <div class="workhours-overview-column">
                    {{ workhour.reason ? workhour.reason : '' }}
                </div>
            </div>

            <div class="workhours-overview-line">
                <div class="workhours-overview-column hash-column"></div>
                <div class="workhours-overview-column"></div>
                <div class="workhours-overview-column"></div>
                <div class="workhours-overview-column"></div>
                <div class="workhours-overview-column"></div>
                <div class="workhours-overview-column"></div>
                <div class="workhours-overview-column"></div>
                <div class="workhours-overview-column"></div>

                <div class="workhours-overview-column totals-column">
                    {{ totalSecs }}
                </div>
                <div class="workhours-overview-column"></div>
            </div>
        </div>

        <spinner v-if="loading" />
    </div>
</template>
<script>
import ActionsMenu from '../components/ActionsMenu';
import ActionsMenuItem from '../components/ActionsMenuItem';
import userApi from '../api/user';
import employeeApi from '../api/employee';
import api from '../api/api.js';
import { months, timePattern } from '../shared/schedule';
import Spinner from '../components/loader/Spinner';
import moment from 'moment';
import { showInfoMessage, showErrorMessage } from '../dialogs';
import _ from 'lodash';
import IdParamMixin from '../mixins/id-param';
import localization from '../mixins/localization';

export default {
    mixins: [IdParamMixin('employeeIdParam'), localization],
    components: {
        Spinner,
        ActionsMenu,
        ActionsMenuItem,
    },
    data() {
        const time = moment();
        return {
            loading: false,
            employees: null,
            workhours: null,
            totalSecs: null,
            months: [...months],
            selectedEmployee: '*',
            selectedMonth: time.month() + 1,
            selectedYear: time.year(),
            sortBy: 'day',
        };
    },
    mounted() {
        this.load();
    },
    computed: {
        employeeIdParam() {
            return this.$route.query.employeeId;
        },
        yearOptions() {
            return _.reverse(_.range(1970, moment().year() + 1));
        },
        filterPayload() {
            return {
                sortBy: this.sortBy,
                year: this.selectedYear,
                month: this.selectedMonth,
                forEmployeeId: this.selectedEmployee,
            };
        },
    },
    methods: {
        moment,
        load() {
            if (this.employeeIdParam) {
                this.selectedEmployee = this.employeeIdParam;
            }
            userApi.listUsers().then((response) => {
                this.employees = response.data.data;
            });

            this.showWorkhours();
        },
        restore(workhour) {
            Object.keys(workhour.original).forEach((key) => {
                workhour[key] = workhour.original[key];
            });
            workhour.error = false;
        },
        updateDayStartEmployee(workhour) {
            return this.saveTime(
                workhour,
                'dayStart',
                this.trans('pages.employee_workhours.workday_start_updated')
            );
        },
        updatePauseStartEmployee(workhour) {
            return this.saveTime(
                workhour,
                'pauseStart',
                this.trans('pages.employee_workhours.lunch_break_start_updated')
            );
        },
        updatePauseEndEmployee(workhour) {
            return this.saveTime(
                workhour,
                'pauseEnd',
                this.trans('pages.employee_workhours.lunch_break_end_updated')
            );
        },
        updateDayEndEmployee(workhour) {
            return this.saveTime(
                workhour,
                'dayEnd',
                this.trans('pages.employee_workhours.workday_end_updated')
            );
        },
        saveTime(workhour, type, successMsg) {
            if (workhour[type] == '') {
                return;
            }

            if (!workhour[type].match(timePattern)) {
                workhour.error = true;
                workhour.saving = false;
                return showErrorMessage(
                    this.trans('pages.employee_workhours.invalid_input_error')
                );
            }

            workhour.error = false;
            workhour.saving = true;

            employeeApi
                .updateWorktime(
                    workhour.employee.ID,
                    workhour.day,
                    workhour[type],
                    type
                )
                .then(() => {
                    workhour.error = false;
                    workhour.saving = false;
                    workhour.original[type] = workhour[type];
                    showInfoMessage(successMsg);
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans('pages.employee_workhours.unknown_error')
                    );
                    workhour.error = true;
                    workhour.saving = false;
                    workhour[type] = workhour.original[type];
                });
        },
        openExport(endpoint) {
            const url = api.createGetUrl(endpoint, this.filterPayload);
            window.open(url);
        },
        exportTargetTimes() {
            this.openExport('/employee/exportTargetTimes/');
        },
        exportWorkhours() {
            this.openExport('/employee/exportTimeRegistrations/');
        },
        durationToHoursMinutesSeconds(duration) {
            const padder = (nr) => {
                const str = nr.toString();
                const pad = '00';
                return pad.substring(0, pad.length - str.length) + str;
            };
            return (
                padder(Math.floor(duration.asHours())) +
                ':' +
                padder(duration.minutes()) +
                ':' +
                padder(duration.seconds())
            );
        },
        showWorkhours() {
            this.loading = true;

            employeeApi
                .listAllTimeRegistrations(this.filterPayload)
                .then((response) => {
                    const workhours = response.data.data;
                    let totalSecs = 0;
                    workhours.forEach((workhour) => {
                        workhour.dayStart = workhour.dayStart
                            ? moment(workhour.dayStart).format('HH:mm:ss')
                            : '/';
                        workhour.pauseStart = workhour.pauseStart
                            ? moment(workhour.pauseStart).format('HH:mm:ss')
                            : '/';
                        workhour.pauseEnd = workhour.pauseEnd
                            ? moment(workhour.pauseEnd).format('HH:mm:ss')
                            : '/';
                        workhour.dayEnd = workhour.dayEnd
                            ? moment(workhour.dayEnd).format('HH:mm:ss')
                            : '/';
                        workhour.totalBreaks =
                            this.durationToHoursMinutesSeconds(
                                moment.duration(workhour.totalBreaks, 'second')
                            );

                        workhour.totalWorked =
                            workhour.totalWorked > 0 ? workhour.totalWorked : 0;
                        totalSecs += workhour.totalWorked;
                        workhour.totalWorked =
                            this.durationToHoursMinutesSeconds(
                                moment.duration(workhour.totalWorked, 'second')
                            );
                        workhour.original = {
                            dayStart: workhour.dayStart,
                            pauseStart: workhour.pauseStart,
                            pauseEnd: workhour.pauseEnd,
                            dayEnd: workhour.dayEnd,
                        };

                        workhour.saving = false;
                        workhour.error = false;
                    });

                    this.workhours = workhours;
                    this.totalSecs = this.durationToHoursMinutesSeconds(
                        moment.duration(totalSecs, 'second')
                    );
                })
                .finally(() => {
                    this.loading = false;
                });
        },
    },
};
</script>
